import { ApprovalDeviceIdentity } from '../devices/DeviceIdentity';
import { jobDeviceDescription } from '@api/devices/identity-contracts';
import { InstallCompatibilityFacts } from './InstallCompatibilityFacts';
import type { JobRequest, JobView } from '@api/devices/contracts';
import { RUNBOOK_UPDATE_REQUIRED } from '@api/operations/runbook-compatibility';
import { WINDOWS_ARM_REINSTALL } from '@api/devices/agent-platform';
import { WindowsPatchFacts } from './WindowsPatchFacts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Copy, KeyRound, ShieldCheck, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { approvalKeyOptions, approveJob, cancelJob, checkJob, createJob, isStatus, isRunbookUpdateRequired, isWindowsArmReinstallRequired, jobApprovalOptions, registerApprovalKey, rejectJob, uncheckJob } from '../lib/api';
import { formatBytes } from '../lib/format';
import { absolute, ago, duration } from '../lib/time';
import { createKey, describeKeyError, signWithKey, webauthnAvailable } from '../lib/webauthn';
import { recordPath } from '../records/paths';
import { useScope } from '../scope/scope';
import { activityQueryKeys, useApprovalKeys, useJob } from '../search/hooks';
import { useSession } from '../session/session';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import { CopyButton } from '../ui/misc';
import { useToast } from '../ui/toast';
import { JobParameters, JobStateBadge, ReviewList } from './meta';
import { RunbookOutputSummary } from './RunbookOutputSummary';

/** What the signed-in person may do in this workspace, for hiding controls they cannot use. */
/** A device as job surfaces need it: identity plus the scope that decides permissions. */
export interface DeviceRef { id: string; title: string; organizationId: string | null; siteId: string | null }

/**
 * What this person may do. With a device scope, only memberships covering that device count, as on the
 * server; without one (workspace-level screens) any membership counts. `null` means not known yet.
 */
export function useCapabilities(scope?: { organizationId: string | null; siteId: string | null } | null) {
  return capabilitiesFrom(useScope().memberships, scope);
}

/** `useCapabilities` for any number of scopes, for lists (hooks can't run per row). */
export function capabilitiesFrom(memberships: ReturnType<typeof useScope>['memberships'], scope?: { organizationId: string | null; siteId: string | null } | null) {
  const covering = scope === undefined ? memberships : !scope ? [] : memberships.filter(m =>
    (m.organizationId === null || m.organizationId === scope.organizationId) && (m.siteId === null || m.siteId === scope.siteId));
  const caps = new Set(covering.flatMap(m => m.capabilities));
  return { diagnostics: caps.has('diagnostics.run'), scripts: caps.has('scripts.run'), approve: caps.has('jobs.approve'), enroll: caps.has('devices.enroll'),
    remote: caps.has('remote.connect') };
}

export function useJobActions() {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const refresh = () => client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId
    && ['jobs', 'jobs-history', 'device-jobs', 'job', ...activityQueryKeys, 'telemetry'].includes(String(q.queryKey[2])) });
  const failed = (title: string) => (error: unknown) => toast({ tone: 'warning', title,
    body: isWindowsArmReinstallRequired(error) ? WINDOWS_ARM_REINSTALL : isRunbookUpdateRequired(error) ? RUNBOOK_UPDATE_REQUIRED : isStatus(error, 403) ? 'You don’t have permission for that, or the security key check failed.' : isStatus(error, 409) ? 'The job changed state or the device can’t take it. Refresh and check.' : 'Try again.' });
  const create = useMutation({ mutationFn: (job: JobRequest) => createJob(mspId, job), onSuccess: () => void refresh(), onError: failed('The job couldn’t be requested') });
  const cancel = useMutation({ mutationFn: (id: string) => cancelJob(mspId, id), onSuccess: () => void refresh(), onError: failed('The job couldn’t be canceled') });
  const reject = useMutation({ mutationFn: ({ id, note }: { id: string; note: string }) => rejectJob(mspId, id, note), onSuccess: () => void refresh(), onError: failed('The job couldn’t be rejected') });
  // A check involves no key: say who may make one, or that the job no longer needs one (WRMM-226).
  const checkFailed = (title: string) => (error: unknown) => toast({ tone: 'warning', title,
    body: isStatus(error, 403) ? 'Only the person who asked for this job, or someone who may approve jobs or run this kind of job on its device, can do that.'
      : isStatus(error, 409) ? 'It no longer needs a check: it was checked already, its result changed, or its device was removed. Refresh to see it.' : 'Try again.' });
  const check = useMutation({ mutationFn: ({ id, note }: { id: string; note: string }) => checkJob(mspId, id, note), onSuccess: () => void refresh(), onError: checkFailed('The job couldn’t be marked as checked') });
  const uncheck = useMutation({ mutationFn: (id: string) => uncheckJob(mspId, id), onSuccess: () => void refresh(), onError: checkFailed('The check couldn’t be undone') });
  return { create, cancel, reject, check, uncheck, refresh };
}

/** Registers this person's first (or an additional) approval key. */
export function RegisterKeyButton({ label = 'Register a security key', onDone, variant = 'secondary' }: { label?: string; onDone?: () => void; variant?: 'secondary' | 'primary' | 'brand' }) {
  const client = useQueryClient();
  const { refresh } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // A first key for someone with an authenticator app or recovery codes needs one of their codes (WRMM-134).
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState('');
  const register = async () => {
    setBusy(true);
    try {
      const options = await approvalKeyOptions();
      if (options.needsCode && !code.trim()) { setAskCode(true); return; }
      const confirmation = options.confirm ? await signWithKey(options.confirm) : undefined; // An existing key vouches for the new one.
      const response = await createKey(options.registration);
      await registerApprovalKey(`Security key added ${new Date().toLocaleDateString()}`, response, confirmation, options.needsCode ? code.trim() : undefined);
      setAskCode(false); setCode('');
      await client.invalidateQueries({ queryKey: ['approval-keys'] });
      refresh();
      toast({ title: 'Security key registered', body: 'You can now approve jobs with it.' });
      onDone?.();
    } catch (error) {
      toast({ tone: 'warning', title: 'The key wasn’t registered', body: isStatus(error, 403)
        ? askCode ? 'That code was refused.' : 'Adding a key needs a touch of one you already have. If every key was removed, an operator must restore approval access.'
        : isStatus(error, 429) ? 'Too many wrong codes: your authenticator is locked for 15 minutes.'
        : (error as { status?: number })?.status ? 'The server did not accept it.' : describeKeyError(error) });
    } finally { setBusy(false); }
  };
  if (!webauthnAvailable()) return <p className="text-[13px] text-attention">Security keys need a secure address: open the console at localhost or over HTTPS.</p>;
  const button = <Button variant={variant} onClick={() => void register()} disabled={busy || (askCode && !code.trim())}><KeyRound className="size-4" />{busy ? 'Waiting for your key…' : askCode ? 'Confirm and register' : label}</Button>;
  if (!askCode) return button;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <input aria-label="Code from your authenticator app, or a recovery code" placeholder="Authenticator or recovery code" maxLength={40} value={code}
        onChange={e => setCode(e.target.value)} className="h-9 w-56 rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50" />
      {button}
    </span>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return <div className="grid grid-cols-[110px_1fr] gap-3 py-1.5 text-[13px]"><dt className="text-ink-3">{label}</dt><dd className="min-w-0 text-ink">{children}</dd></div>;
}

/** The approval surface: the exact action, its review notes, then a key touch bound to that action. */
export function ApprovalDialog({ job: summary, open, onOpenChange }: { job: JobView | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const keys = useApprovalKeys();
  // Lists leave out the script: show and approve only the job as read back on its own, script included.
  const full = useJob(open && summary ? summary.id : null);
  const loaded = full.data && full.data.id === summary?.id && full.data.script !== null ? full.data : null;
  const job = loaded ?? summary;
  const { approve: canApprove } = useCapabilities(job);
  const { reject } = useJobActions();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  // A rejection reason belongs to one job: never carry it over to the next one opened.
  useEffect(() => setNote(''), [summary?.id, open]);
  if (!job) return null;
  const high = job.review.filter(f => f.severity === 'high').length;
  const release = job.operation === 'agent_update' ? job.release : null;
  const approve = async () => {
    setBusy(true);
    try {
      const options = await jobApprovalOptions(mspId, job.id);
      const response = await signWithKey(options);
      await approveJob(mspId, job.id, response);
      await client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId });
      toast({ title: 'Approved', body: `${job.title} is queued for ${jobDeviceDescription(job)}.` });
      onOpenChange(false);
    } catch (error) {
      toast({ tone: 'warning', title: 'Not approved', body: (error as { status?: number })?.status ? 'The server refused the approval. The job may have changed or expired.' : describeKeyError(error) });
    } finally { setBusy(false); }
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/45 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col overflow-hidden rounded-t-2xl border border-line bg-raised shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[6vh] sm:w-[min(720px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            <span className="mt-0.5 flex size-9 items-center justify-center rounded-xl bg-attention-soft text-attention"><KeyRound className="size-5" /></span>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">Approve {job.operation === 'script' ? (job.shell === 'zsh' ? 'Zsh script' : job.shell === 'bash' ? 'Bash script' : 'PowerShell script') : release ? `the agent update to ${release.version}` : job.title}</Dialog.Title>
              <Dialog.Description className="text-[13px] text-ink-2">Your key signs this exact action. Any change to the {job.operation === 'windows_patch' ? 'patch plan' : release ? 'release' : 'script'}, device, account or expiry needs a new approval.</Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <dl className="divide-y divide-line/70 rounded-xl border border-line px-3">
              <Fact label="Device"><Link to={recordPath(mspId, 'device', job.deviceId)} className="block hover:underline"><ApprovalDeviceIdentity device={{ id: job.deviceId, title: job.deviceTitle ?? 'Removed device', hostname: job.deviceHostname }} /></Link>
                <bdi dir="auto" className="text-ink-3">{[job.organizationName, job.siteName].filter(Boolean).join(' · ')}</bdi></Fact>
              <Fact label="Runs as">{release ? `${job.shell === 'bash' || job.shell === 'zsh' ? 'root' : 'SYSTEM'} · replaces the WITTCO agent · gives up after ${duration(job.timeoutSeconds * 1000)}`
                : <>{job.runAs === 'system' ? (job.shell === 'bash' || job.shell === 'zsh' ? 'root' : 'SYSTEM') : 'The signed-in user'} · {job.operation === 'windows_patch'
                  ? `Windows Update worker · caller budget ${duration(job.timeoutSeconds * 1000)}; Windows servicing may continue after timeout`
                  : <>{job.shell === 'zsh' ? `Zsh · ${job.architecture}` : job.shell === 'bash' ? `Bash · ${job.architecture}` : '64-bit PowerShell'} · stops after {duration(job.timeoutSeconds * 1000)}</>}</>}</Fact>
              <Fact label="Requested">{job.automatic ? 'Automatically, when a newer signed release came out' : job.requestedByMe ? 'By you' : 'By a teammate'} {ago(job.requestedAt)} — “{job.reason}”</Fact>
              <Fact label="Valid until">{absolute(job.expiresAt)}</Fact>
              {Object.keys(job.parameters).length > 0 && <Fact label="Parameters"><JobParameters parameters={job.parameters} /></Fact>}
            </dl>
            <WindowsPatchFacts job={job} />
            <InstallCompatibilityFacts job={job} />
            {release ? <ReleaseFacts release={release} /> : <>
            {job.operation !== 'windows_patch' &&
            <section>
              <h3 className="mb-2 text-[13px] font-semibold text-ink-2">Review notes {high > 0 && <span className="text-offline">· {high} high</span>}</h3>
              <ReviewList findings={job.review} />
              <p className="mt-2 text-[12px] text-ink-3">Rule-based notes on the script text. They are not a security scan; read the script itself.</p>
            </section>}
            <section>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <h3 className="text-[13px] font-semibold text-ink-2">{job.operation === 'windows_patch' ? 'Exact frozen patch plan' : 'Exact script'}</h3>
                <span className="flex items-center gap-1 font-mono text-[11.5px] text-ink-3" title="SHA-256 of the exact bytes that will run">SHA-256 {job.scriptSha256.slice(0, 16)}…<CopyButton value={job.scriptSha256} label="Copy SHA-256" /></span>
              </div>
              {loaded ? <pre className="max-h-72 overflow-auto rounded-xl border border-line bg-sunken p-3 font-mono text-[12.5px] leading-relaxed text-ink">{loaded.script}</pre>
                : <p className="rounded-xl border border-line bg-sunken p-3 text-[13px] text-ink-3">{full.isError ? 'The script couldn’t be loaded, so this can’t be approved yet. Close and try again.' : 'Loading the exact script…'}</p>}
            </section></>}
          </div>
          <div className="space-y-3 border-t border-line bg-sunken/40 px-5 py-4">
            {!canApprove ? <p className="text-[13px] text-ink-2">You can’t approve jobs in this workspace. Someone with approval rights will see it in Work.</p>
              : keys.data && keys.data.length === 0 ? (
                <div className="flex flex-wrap items-center gap-3"><p className="flex-1 text-[13px] text-ink-2">Approvals need a registered security key. Register one, then approve.</p><RegisterKeyButton variant="primary" /></div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note for a rejection (optional)" aria-label="Rejection note"
                    className="h-9 min-w-0 flex-1 rounded-lg border border-line bg-panel px-3 text-[13px] outline-none focus:border-brand/50" />
                  <Button variant="danger" disabled={busy || reject.isPending} onClick={() => reject.mutate({ id: job.id, note }, { onSuccess: () => onOpenChange(false) })}>Reject</Button>
                  <Button variant="primary" disabled={busy || !loaded} onClick={() => void approve()}><ShieldCheck className="size-4" />{busy ? 'Touch your security key…' : 'Approve with security key'}</Button>
                </div>
              )}
            {job.requestedByMe && canApprove && <p className="text-[12px] text-ink-3">You requested this job, so this will be recorded as self-approved.</p>}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** What an agent update installs: the signed release, as the device will check it. */
export function ReleaseFacts({ release }: { release: NonNullable<JobView['release']> }) {
  return (
    <section>
      <h3 className="mb-2 text-[13px] font-semibold text-ink-2">Release</h3>
      <dl className="divide-y divide-line/70 rounded-xl border border-line px-3">
        <Fact label="Version">{release.version} <span className="text-ink-3">· signed by a WITTCO release key</span></Fact>
        <Fact label="Package"><span className="inline-flex items-center gap-1 font-mono text-[12px]">{formatBytes(release.size)} · SHA-256 {release.sha256.slice(0, 16)}…<CopyButton value={release.sha256} label="Copy SHA-256" /></span></Fact>
        <Fact label="Security floor">{release.floor}</Fact>
      </dl>
      <p className="mt-2 text-[12px] text-ink-3">The device installs it only if the release signature and the downloaded package check out, and puts the previous version back if the new one doesn’t start and stay up.</p>
    </section>
  );
}

/** Output exactly as the device reported it, rendered as text. */
export function JobOutput({ job }: { job: JobView }) {
  const run = job.run;
  if (!run) return null;
  const text = [run.stdout, run.stderr && `--- errors ---\n${run.stderr}`].filter(Boolean).join('\n');
  const took = run.startedAt && run.finishedAt ? duration(Date.parse(run.finishedAt) - Date.parse(run.startedAt)) : null;
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-3">
        {run.exitCode !== null && <span>Exit code <span className="font-mono text-ink-2">{run.exitCode}</span></span>}
        {took && <span>Took {took}</span>}
        {run.finishedAt && <span>Finished {ago(run.finishedAt)}</span>}
        {run.truncated && <span className="text-attention">Output was truncated by the device</span>}
        <span className="flex-1" />
        {text && <CopyButton value={text} label="Copy output" />}
      </div>
      {text ? <>
        <pre className="max-h-96 overflow-auto rounded-xl border border-line bg-[#0a1620] p-3 font-mono text-[12.5px] leading-relaxed text-[#d6e2ea]">{text}</pre>
        <RunbookOutputSummary key={run.id} stdout={run.stdout} runbookId={job.operation === 'runbook' ? job.runbookId : null} />
      </> : <p className="text-[13px] text-ink-3">{['running', 'delivered'].includes(run.status) ? 'Waiting for output…' : 'No output.'}</p>}
    </div>
  );
}

/** States someone can mark as checked (WRMM-91): the job ended without a result to rely on. */
export const CHECKABLE: JobView['state'][] = ['failed', 'unknown_outcome'];

/** Removing its device closed this failed or unknown-outcome job before anyone checked it (WRMM-226): it needs no check. */
export const closedByRemoval = (job: JobView) => CHECKABLE.includes(job.state) && !job.checked && !!job.deviceRemovedAt;

/** Who may mark a job as checked, as the server decides: its requester, an approver, or whoever may request that kind of job. */
function useMayCheck(job: JobView) {
  const caps = useCapabilities(job);
  // No one holds a capability over a removed device, and its jobs need no check (WRMM-226).
  if (job.deviceRemovedAt) return false;
  return job.requestedByMe || caps.approve || (job.operation === 'script' || job.operation === 'windows_patch' ? caps.scripts : job.operation === 'agent_update' ? caps.enroll : caps.diagnostics);
}

/** A job in a list: what, where, state; expands to the script, notes, approval and output. */
export function JobRow({ job, showDevice = true, defaultOpen = false, onApprove }: { job: JobView; showDevice?: boolean; defaultOpen?: boolean; onApprove?: (job: JobView) => void }) {
  const { mspId } = useScope();
  const [open, setOpen] = useState(defaultOpen);
  const [checking, setChecking] = useState(false);
  const { cancel } = useJobActions();
  const { approve } = useCapabilities(job);
  const mayCheck = useMayCheck(job);
  const checkable = CHECKABLE.includes(job.state) && !job.checked && mayCheck;
  // An agent update the device already has can't be stopped: its updater is out of reach of a cancel.
  const cancellable = !job.canceledAt && ['awaiting_approval', 'queued', 'delivered', 'running'].includes(job.state) && (job.requestedByMe || approve)
    && !(job.operation === 'agent_update' && job.run);
  return (
    <div className="border-b border-line/70 last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3">
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <ChevronDown className={cn('size-4 shrink-0 text-ink-3 transition-transform', !open && '-rotate-90')} />
          <span className="min-w-0">
            <span className="block truncate font-medium">{job.title}{job.effect === 'change' && <span className="ml-2 rounded bg-sunken px-1.5 py-px text-[11px] font-medium text-ink-2">changes device</span>}</span>
            <span className="block truncate text-[12.5px] text-ink-3">
              {showDevice && <><Link to={recordPath(mspId, 'device', job.deviceId)} className="font-mono text-ink-2 hover:underline" onClick={e => e.stopPropagation()}>{jobDeviceDescription(job)}</Link> · </>}
              {job.automatic ? 'Automatic' : job.requestedByMe ? 'You' : 'Teammate'} · {ago(job.requestedAt)} · “{job.reason}”
            </span>
          </span>
        </button>
        <JobStateBadge state={job.state} />
        {job.checked && <span title={`Checked ${job.checked.byMe ? 'by you' : 'by a teammate'} ${ago(job.checked.at)}`}
          className="inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-full bg-healthy-soft px-2.5 text-[12.5px] font-medium text-healthy"><Check className="size-3.5" />Checked</span>}
        {closedByRemoval(job) && <span title={`Closed when its device was removed ${ago(job.deviceRemovedAt!)}`}
          className="inline-flex h-6 items-center whitespace-nowrap rounded-full bg-sunken px-2.5 text-[12.5px] font-medium text-ink-2">Device removed</span>}
        {job.state === 'awaiting_approval' && onApprove && approve && <Button size="sm" variant="primary" onClick={() => onApprove(job)}><KeyRound className="size-3.5" />Review</Button>}
        {cancellable && <Button size="sm" variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(job.id)}>Cancel</Button>}
        {checkable && !checking && <Button size="sm" variant="secondary" onClick={() => setChecking(true)}><Check className="size-3.5" />Mark as checked</Button>}
      </div>
      {checking && checkable && <CheckForm job={job} onDone={() => setChecking(false)} />}
      {open && <JobDetail job={job} />}
    </div>
  );
}

/** Marks a failed or unknown-outcome job as checked, with an optional note on what was found. */
function CheckForm({ job, onDone }: { job: JobView; onDone: () => void }) {
  const { check } = useJobActions();
  const [note, setNote] = useState('');
  return (
    <form className="flex flex-wrap items-center gap-2 px-4 pb-3 pl-10" onSubmit={e => { e.preventDefault(); check.mutate({ id: job.id, note: note.trim() }, { onSuccess: onDone }); }}>
      <input value={note} onChange={e => setNote(e.target.value)} maxLength={500} autoFocus placeholder="What did you find? (optional)" aria-label="What you found"
        className="h-9 min-w-0 flex-1 rounded-lg border border-line bg-panel px-3 text-[13px] outline-none focus:border-brand/50" />
      <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      <Button size="sm" variant="primary" type="submit" disabled={check.isPending}><Check className="size-3.5" />Mark as checked</Button>
      <p className="w-full text-[12px] text-ink-3">It moves out of Needs a check and keeps its outcome ({job.state === 'failed' ? 'failed' : 'outcome unknown'}). You can undo it.</p>
    </form>
  );
}

function JobDetail({ job: summary }: { job: JobView }) {
  const [showScript, setShowScript] = useState(false);
  // Lists omit the script and output; the expanded row reads the full job.
  const full = useJob(summary.id);
  const job = full.data ?? summary;
  const { uncheck } = useJobActions();
  const mayCheck = useMayCheck(job);
  return (
    <div className="space-y-3 px-4 pb-4 pl-10">
      {job.checked && (
        <p className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-ink-2"><Check className="size-3.5 text-healthy" />
          Checked {job.checked.byMe ? 'by you' : 'by a teammate'} {ago(job.checked.at)}{job.checked.note && ` — “${job.checked.note}”`}
          {mayCheck && <button type="button" disabled={uncheck.isPending} onClick={() => uncheck.mutate(job.id)}
            className="ml-1 inline-flex h-6 items-center rounded-full border border-line bg-panel px-2.5 text-[12.5px] font-medium text-ink-2 hover:bg-hover hover:text-ink disabled:opacity-50">Undo</button>}
        </p>
      )}
      {closedByRemoval(job) && (
        <p className="text-[12.5px] text-ink-2">Closed when its device was removed {ago(job.deviceRemovedAt!)}. Nothing more can be done there, so it needs no check; its outcome stays.</p>
      )}
      {job.automatic && (
        <p className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-ink-2"><Check className="size-3.5 text-healthy" />
          Approved by WITTCO’s signature on release {job.release?.version ?? ''}, which the device checked itself; no key touch needed.</p>
      )}
      {job.approval && (
        <p className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-ink-2">
          {job.approval.decision === 'approved' ? <Check className="size-3.5 text-healthy" /> : <X className="size-3.5 text-offline" />}
          {job.approval.decision === 'approved' ? 'Approved' : 'Rejected'} {job.approval.decidedByMe ? 'by you' : 'by a teammate'} {ago(job.approval.decidedAt)}
          {job.approval.method === 'webauthn' && ` with a security key${job.approval.userVerified ? ' (PIN or biometric verified)' : ''}`}
          {job.approval.selfApproved && ' · self-approved'}{job.approval.note && ` — “${job.approval.note}”`}
        </p>
      )}
      {job.state === 'unknown_outcome' && <p className="rounded-lg bg-attention-soft px-3 py-2 text-[13px] text-attention">The device took this job but its result never arrived. Check the device before running it again; it may already have run.</p>}
      <WindowsPatchFacts job={job} />
            <InstallCompatibilityFacts job={job} />
      <JobOutput job={job} />
      {job.review.length > 1 && <ReviewList findings={job.review.filter(f => f.line !== null || f.severity !== 'info')} compact />}
      {job.release ? <p className="text-[12.5px] text-ink-2">Release {job.release.version} · {formatBytes(job.release.size)} · SHA-256 <span className="font-mono">{job.release.sha256.slice(0, 16)}…</span></p> : <>
      <button type="button" onClick={() => setShowScript(s => !s)} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-ink-2 hover:text-ink">
        <Copy className="size-3.5" />{showScript ? 'Hide' : 'Show'} the exact {job.operation === 'windows_patch' ? 'frozen patch plan' : 'script'}
      </button>
      {showScript && (job.script === null ? <p className="text-[12.5px] text-ink-3">{full.isError ? 'The script couldn’t be loaded.' : 'Loading the script…'}</p>
        : <pre className="max-h-64 overflow-auto rounded-xl border border-line bg-sunken p-3 font-mono text-[12px] text-ink">{job.script}</pre>)}</>}
    </div>
  );
}

